import { useEffect, useState } from "react";
import { ArtistDTO, Song } from "../../view/spotipitan/types";
import { getPlaylistPosterUrl, getSongImageUrl, normalizeFileUrl, BACKEND_URL } from "../../../utils/api";
import notFoundImage from "../../../images/not-found.png";

interface UseCachedImageOptions {
    song?: Song;
    playlist?: ArtistDTO;
    big?: boolean;
    src?: string;
    isVisible?: boolean;
}

const isYoutubeUrl = (url: string): boolean => {
    return url.includes("yt3.googleusercontent.com") ||
           url.includes("ytimg.com") ||
           url.includes("googleusercontent.com") ||
           url.includes("youtube.com") ||
           url.includes("youtu.be");
};

const wrapProxyIfNeeded = (url: string | undefined): string | undefined => {
    if (!url) return url;
    if (isYoutubeUrl(url)) {
        return `${BACKEND_URL}cache-image?url=${encodeURIComponent(url)}`;
    }
    return url;
};

export const useCachedImage = ({ song, playlist, big = false, src, isVisible = true }: UseCachedImageOptions) => {
    const [imageUrl, setImageUrl] = useState<string | undefined>(wrapProxyIfNeeded(normalizeFileUrl(src)));
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isVisible) {
            return;
        }
        let isMounted = true;

        const resolveImage = async () => {
            // Caso 1: Se proporciona una URL directa (src)
            if (src && !song && !playlist) {
                const resolvedSrc = normalizeFileUrl(src);
                if (isMounted) {
                    setImageUrl(wrapProxyIfNeeded(resolvedSrc));
                    setLoading(false);
                }
                return;
            }

            // Caso 2: Se proporciona un objeto Song
            if (song) {
                if (isMounted) setLoading(true);
                try {
                    const url = await getSongImageUrl(song, big);
                    if (isMounted) {
                        setImageUrl(wrapProxyIfNeeded(url));
                        setLoading(false);
                    }
                } catch (error) {
                    console.error("Error resolving song image:", error);
                    if (isMounted) {
                        setImageUrl(wrapProxyIfNeeded(notFoundImage));
                        setLoading(false);
                    }
                }
                return;
            }

            // Caso 3: Se proporciona un objeto Playlist
            if (playlist) {
                if (isMounted) setLoading(true);
                try {
                    const url = await getPlaylistPosterUrl(playlist);
                    if (isMounted) {
                        setImageUrl(wrapProxyIfNeeded(url));
                        setLoading(false);
                    }
                } catch (error) {
                    console.error("Error resolving playlist image:", error);
                    if (isMounted) {
                        setImageUrl(wrapProxyIfNeeded(notFoundImage));
                        setLoading(false);
                    }
                }
                return;
            }

            // Caso 4: Ninguno (fallback)
            if (isMounted) {
                setImageUrl(wrapProxyIfNeeded(src || notFoundImage));
                setLoading(false);
            }
        };

        resolveImage();

        return () => {
            isMounted = false;
        };
    }, [song, playlist, big, src, isVisible]);

    return { imageUrl, loading };
};
